import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Row,
  Col,
  Typography,
  Form,
  Input,
  Button,
  message,
  InputNumber,
  Select,
  Space,
  Radio,
  DatePicker,
  Modal,
  Empty,
  AutoComplete,
  Tag,
  Spin,
  Tooltip,
} from "antd";
import btnStyles from "../../../styles/Custom/Button.module.css";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { validateConfig } from "../../../config/validate.config";
import {
  addAppointmentApi,
  getPatientDetailsApi,
  getPatientOpAdmissionsApi,
  getSlotsApi,
  getPatientListApi,
} from "../../../services/receptionist.services";
import dayjs from "dayjs";
import { genderKeys } from "../receptionKeys";
import PractitionerSearch from "./PractitionerSearch";
import { getFacilityListApi } from "../../../services/facilities.services";
import styles from "./AppointsModal.module.css";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID } from "../../../constants/defaultKeys";
import { UserContext } from "../../../context/UserContext";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import { capitalizeFirstLetter, debounce } from "../../../helpers/utility";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "../receptionistMobilePickerProps";
import "../receptionistMobileForms.css";

const { Option } = Select;

const formatSlotTime = (t) => {
  if (!t) return "";
  return dayjs(t, "HH:mm").format("h:mm A");
};

const getSlotBucket = (t) => {
  const hour = dayjs(t, "HH:mm").hour();
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
};

const AppointsModal = ({ open, setOpen, refresh, editData }) => {
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const { userDetails } = useContext(UserContext);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const defaultFacilityId = useMemo(
    () => Number(getCookie(FacilityID)) || userDetails?.currFacility || null,
    [userDetails?.currFacility]
  );
  const [facilityId, setFacilityId] = useState(defaultFacilityId);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEndSlot, setSelectedEndSlot] = useState(null);
  const [appointmentFor, setAppointmentFor] = useState(null);
  const [opAdmissions, setOpAdmissions] = useState([]);
  const [opAdmissionsLoading, setOpAdmissionsLoading] = useState(false);
  const [patientText, setPatientText] = useState("");
  const [patientOptions, setPatientOptions] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [practitionerText, setPractitionerText] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customDateOpen, setCustomDateOpen] = useState(false);

  const orgAccentColor = userDetails?.activeOrg?.color || "#6941C6";

  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }

  function disableApptDate(current) {
    return current && current < dayjs().startOf("day");
  }

  useEffect(() => {
    let ignore = false;
    getFacilityListApi()
      .then((res) => {
        if (ignore) return;
        setFacilities(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!ignore) setFacilities([]);
      });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    if (editData?.id) {
      setSelectedId(editData.id);
    }

    if (!editData?.id && defaultFacilityId) {
      setFacilityId(defaultFacilityId);
    }

    if (!editData?.id) {
      setPatientOptions([]);
      setPatientText("");
      setSelectedId(null);
    }
  }, [open, editData?.id, defaultFacilityId]);

  const fetchPatients = useCallback(
    (searchedValue) => {
      if (!facilityId) return;
      const q = (searchedValue || "").trim();
      if (q.length < 1) {
        setPatientOptions([]);
        return;
      }

      setLoadingPatients(true);
      const params = new URLSearchParams({
        page: "1",
        limit: "20",
        facility_id: String(facilityId),
        search: q,
      });

      getPatientListApi(params)
        .then((res) => {
          const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
          setPatientOptions(
            rows.map((p) => ({
              key: p?.id,
              value: `${capitalizeFirstLetter(
                p?.first_name
              )} ${capitalizeFirstLetter(p?.last_name)}`.trim(),
              patient_code: p?.patient_id,
              label: (
                <Row>
                  <Col span={24}>
                    <span>
                      {capitalizeFirstLetter(p?.first_name)}{" "}
                      {capitalizeFirstLetter(p?.last_name)}
                      {p?.patient_id ? (
                        <Tag style={{ marginLeft: 8 }} color="blue">
                          {p.patient_id}
                        </Tag>
                      ) : null}
                    </span>
                  </Col>
                </Row>
              ),
            }))
          );
        })
        .catch(() => setPatientOptions([]))
        .finally(() => setLoadingPatients(false));
    },
    [facilityId]
  );

  const optimizedPatientSearch = useMemo(
    () => debounce(fetchPatients, 300),
    [fetchPatients]
  );

  const handlePatientSelect = (value, option) => {
    setPatientText(value);
    setSelectedId(option?.key);
  };

  useEffect(() => {
    if (selectedId || editData?.id) {
      getPatientDetailsApi(selectedId || editData?.id)
        .then((res) => {
          form?.setFieldsValue({
            ...res?.data,
            patient_first_name: res?.data?.first_name,
            patient_last_name: res?.data?.last_name,
            dob: res?.data?.dob ? dayjs(res.data.dob) : null,
            patient_mobile: res?.data?.mobile,
            patient: res?.data?.id,
            patient_gender: res?.data?.gender,
          });
          setFacilityId(res?.data?.facility);
          setSelectedId(res?.data?.id);
          const fullName = `${capitalizeFirstLetter(
            res?.data?.first_name || ""
          )} ${capitalizeFirstLetter(res?.data?.last_name || "")}`.trim();
          if (fullName) setPatientText(fullName);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [selectedId, editData?.id, form]);

  const getDoctorDisplayName = (admission) => {
    if (admission?.doctor_name) {
      return admission.doctor_name
        .split(" ")
        .map((part) => capitalizeFirstLetter(part))
        .join(" ")
        .trim();
    }
    if (admission?.doctor_first_name || admission?.doctor_last_name) {
      return `${capitalizeFirstLetter(
        admission.doctor_first_name || ""
      )} ${capitalizeFirstLetter(admission.doctor_last_name || "")}`.trim();
    }
    return "";
  };

  const applyConsultationPractitioner = useCallback(
    (admissionId) => {
      if (!admissionId) {
        setSelectedSlot(null);
        setSelectedEndSlot(null);
        return;
      }

      const admission = opAdmissions.find((item) => item.id === admissionId);
      if (!admission) return;

      const doctorId = admission.doctor ?? admission.doctor_id;
      const doctorName = getDoctorDisplayName(admission);

      if (doctorId) setSelectedPractitioner(doctorId);
      if (doctorName) {
        setPractitionerText(doctorName);
        form.setFieldValue("name", doctorName);
      }
    },
    [opAdmissions, form]
  );

  const getOpAdmissionLabel = (admission) => {
    const consultationId = admission?.op_id || admission?.id;
    const doctor = admission?.doctor_name ? ` — ${admission.doctor_name}` : "";
    const date = admission?.admission_date
      ? ` (${dayjs(admission.admission_date).format("DD MMM, YYYY")})`
      : "";
    return `${consultationId}${doctor}${date}`;
  };

  useEffect(() => {
    if (appointmentFor !== "session" || !selectedId) {
      setOpAdmissions([]);
      form.setFieldValue("op_admission", undefined);
      if (appointmentFor !== "session") {
        setOpAdmissionsLoading(false);
      }
      return;
    }

    let ignore = false;
    setOpAdmissionsLoading(true);
    getPatientOpAdmissionsApi(selectedId)
      .then((res) => {
        if (ignore) return;
        const list = res?.data?.data ?? res?.data ?? [];
        setOpAdmissions(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (ignore) return;
        message.error(
          err?.response?.data?.message || "Failed to load consultations"
        );
        setOpAdmissions([]);
      })
      .finally(() => {
        if (!ignore) setOpAdmissionsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [appointmentFor, selectedId, form]);

  // Availability depends only on doctor + date, so the picked time is dropped
  // only when it genuinely disappears from the refreshed list — editing any
  // other field must not cost the user their selection.
  const selectedSlotRef = useRef(null);
  useEffect(() => {
    selectedSlotRef.current = selectedSlot;
  }, [selectedSlot]);

  useEffect(() => {
    let ignore = false;
    const dateValue = formValues?.appointment_date;
    if (!selectedPractitioner || !dateValue) {
      setSlots([]);
      setSelectedSlot(null);
      setSelectedEndSlot(null);
      setLoadingSlots(false);
      return;
    }
    setLoadingSlots(true);
    getSlotsApi(
      `doctor_id=${selectedPractitioner}&selected_date=${dayjs(
        dateValue
      ).format("YYYY-MM-DD")}`
    )
      .then((res) => {
        if (ignore) return;
        const list = Array.isArray(res?.data?.slots) ? res.data.slots : [];
        setSlots(list);

        const previous = selectedSlotRef.current;
        if (!previous) return;
        const stillOffered = list.find(
          (slot) => slot?.appointment_time === previous
        );
        if (stillOffered) {
          setSelectedEndSlot(stillOffered?.appointment_end_time ?? null);
        } else {
          setSelectedSlot(null);
          setSelectedEndSlot(null);
        }
      })
      .catch(() => {
        if (!ignore) setSlots([]);
      })
      .finally(() => {
        if (!ignore) setLoadingSlots(false);
      });
    return () => {
      ignore = true;
    };
  }, [selectedPractitioner, formValues?.appointment_date]);

  const onFinish = (values) => {
    setLoading(true);

    let calculatedDOB = null;

    if (values?.dob) {
      calculatedDOB = dayjs(values.dob).format("YYYY-MM-DD");
    }

    if (values?.age && !values?.dob) {
      const year = dayjs().year() - values.age;
      calculatedDOB = `${year}-01-01`;
    }

    const { name, ...formData } = values;
    formData.dob = calculatedDOB;

    const appointmentDate =
      values?.appointment_date || formValues?.appointment_date;
    formData.appointment_date = dayjs(appointmentDate).format("YYYY-MM-DD");
    formData.doctor = selectedPractitioner;
    formData.appointment_time = selectedSlot;
    formData.appointment_end_time = selectedEndSlot;
    formData.facility = facilityId;
    // API expects numeric patient id
    formData.patient = selectedId;
    formData.patient_mobile = values?.patient_mobile;
    formData.appointment_for = appointmentFor;
    if (appointmentFor === "session") {
      formData.op_admission = values.op_admission;
    }
    formData.reason_to_visit = values?.reason_to_visit || null;
    addAppointmentApi(formData)
      .then((res) => {
        message.success(res?.data?.message);
        setOpen(false);
        setLoading(false);
        refresh();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message);
        setLoading(false);
      });
  };

  const onValuesChange = (changedValues, values) => {
    setFormValues({ ...values, ...changedValues });
    if ("op_admission" in changedValues) {
      applyConsultationPractitioner(changedValues.op_admission);
    }
  };

  const closeAndReset = () => {
    setOpen(false);
    setLoading(false);
    setPatientOptions([]);
    setSelectedId(null);
    setFacilityId(defaultFacilityId);
    setSelectedPractitioner(null);
    setPatientText("");
    setPractitionerText("");
    setSelectedSlot(null);
    setSelectedEndSlot(null);
    setAppointmentFor(null);
    setOpAdmissions([]);
    setOpAdmissionsLoading(false);
    setSlots([]);
    form?.resetFields();
    setFormValues(null);
  };

  const quickDates = Array.from({ length: 10 }).map((_, i) =>
    dayjs().add(i, "day")
  );
  const selectedDate = formValues?.appointment_date
    ? dayjs(formValues.appointment_date)
    : null;
  /*
   * Booking needs seven separate pieces of state, and several are cleared as a
   * side effect of changing another field (picking a slot then switching the
   * doctor or visit type wipes the slot, for instance). Naming what is missing
   * stops the button reading as broken when it is really just incomplete.
   */
  const missingForBooking = [
    [!selectedId, "patient"],
    [!facilityId, "facility"],
    [!selectedPractitioner, "doctor"],
    [!formValues?.appointment_date, "date"],
    [!selectedSlot, "time slot"],
    [!appointmentFor, "visit type"],
    [appointmentFor === "session" && !formValues?.op_admission, "consultation"],
  ]
    .filter(([isMissing]) => isMissing)
    .map(([, label]) => label);

  const isQuickDateSelected = Boolean(
    selectedDate && quickDates.some((d) => d.isSame(selectedDate, "day"))
  );
  const isCustomDateSelected = Boolean(selectedDate && !isQuickDateSelected);

  const sortedSlots = [...(Array.isArray(slots) ? slots : [])].sort((a, b) => {
    const ta = dayjs(a?.appointment_time, "HH:mm");
    const tb = dayjs(b?.appointment_time, "HH:mm");
    return ta.isBefore(tb) ? -1 : ta.isAfter(tb) ? 1 : 0;
  });

  return (
    <div>
      <Modal
        open={open}
        onCancel={closeAndReset}
        footer={null}
        centered={!isMobile}
        width={isMobile ? "100%" : 920}
        wrapClassName={isMobile ? "reception-modal-fullscreen" : undefined}
        classNames={
          isMobile ? { wrapper: "reception-modal-fullscreen" } : undefined
        }
        styles={
          isMobile
            ? {
                wrapper: { padding: 0, overflow: "hidden" },
                content: {
                  height: "100vh",
                  maxHeight: "100vh",
                  margin: 0,
                  top: 0,
                  padding: 0,
                  borderRadius: 0,
                },
                body: {
                  flex: 1,
                  overflow: "auto",
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                },
              }
            : undefined
        }
        title={
          <div className={styles.modalTitleRow}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Book Appointment
            </Typography.Title>
          </div>
        }
        className={
          isMobile ? `${styles.modal} ${styles.modalMobile}` : styles.modal
        }
        destroyOnClose
        style={{ ["--accent-color"]: orgAccentColor }}
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          ref={formRef}
          onValuesChange={onValuesChange}
        >
          <div className={styles.hiddenFields}>
            <Form.Item
              name="patient_first_name"
              rules={validateConfig.isRequired("First Name")}
            >
              <Input />
            </Form.Item>
            <Form.Item name="patient_last_name">
              <Input />
            </Form.Item>
            <Form.Item
              name="dob"
              rules={[{ required: true, message: "Enter DOB or Age" }]}
            >
              <DatePicker {...mobilePickerProps} disabledDate={disabledDate} />
            </Form.Item>
            <Form.Item name="age_by_dob">
              <InputNumber />
            </Form.Item>
            <Form.Item
              name="patient_gender"
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Select options={genderKeys} />
            </Form.Item>
            <Form.Item
              name="patient_mobile"
              rules={[
                { required: true, message: "Please enter mobile number" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="status" initialValue="scheduled">
              <Input />
            </Form.Item>
            <Form.Item
              name="appointment_date"
              rules={validateConfig.isRequired("Date")}
            >
              <DatePicker {...mobilePickerProps} />
            </Form.Item>
            <Form.Item name="appointment_time">
              <Input />
            </Form.Item>
          </div>

          <div
            className={styles.contentWrap}
            style={{ "--accent-color": orgAccentColor }}
          >
            <div className={styles.topGrid}>
              <Form.Item
                label="Choose Patient"
                required
                className={styles.patientField}
              >
                {editData?.id ? (
                  <Input
                    size="middle"
                    prefix={<SearchOutlined />}
                    value={patientText}
                    readOnly
                    className={inputStyles.mdInput}
                  />
                ) : (
                  <AutoComplete
                    options={patientOptions}
                    className={styles.patientAutoComplete}
                    disabled={!facilityId}
                    onSearch={optimizedPatientSearch}
                    onSelect={handlePatientSelect}
                    onChange={(value) => {
                      setPatientText(value);
                      setSelectedId(null);
                      optimizedPatientSearch(value);
                    }}
                    value={patientText}
                  >
                    <Input
                      size="middle"
                      prefix={<SearchOutlined />}
                      placeholder={
                        facilityId ? "Search Patient" : "Select facility first"
                      }
                      className={inputStyles.mdInput}
                    />
                  </AutoComplete>
                )}
              </Form.Item>

              <Form.Item
                label="Choose Facility"
                required
                className={styles.facilityField}
              >
                <Select
                  allowClear
                  value={facilityId || undefined}
                  placeholder="Choose facility"
                  className={styles.select}
                  onChange={(value) => {
                    setFacilityId(value);
                    setSelectedPractitioner(null);
                    setPractitionerText("");
                    setSelectedId(null);
                    setPatientText("");
                    setPatientOptions([]);
                    setSelectedSlot(null);
                    setSelectedEndSlot(null);
                    form.setFieldsValue({ appointment_date: null, name: null });
                    setFormValues((prev) => ({
                      ...(prev || {}),
                      appointment_date: null,
                    }));
                  }}
                >
                  {facilities.map((f) => (
                    <Option key={f?.id} value={f?.id}>
                      {f?.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Doctor" required className={styles.doctorField}>
                <div className={styles.practitionerWrap}>
                  <PractitionerSearch
                    facilityId={facilityId}
                    setSelectedPractitioner={setSelectedPractitioner}
                    practitionerText={practitionerText}
                    setPractitionerText={setPractitionerText}
                  />
                </div>
              </Form.Item>

              <Form.Item
                label="Visit Type"
                required
                className={styles.visitTypeField}
              >
                <Select
                  value={appointmentFor || undefined}
                  placeholder="Select visit type"
                  className={styles.select}
                  onChange={(v) => {
                    setAppointmentFor(v);
                    if (v !== "session") {
                      form.setFieldValue("op_admission", undefined);
                      setFormValues((prev) => ({
                        ...(prev || {}),
                        op_admission: undefined,
                      }));
                    }
                  }}
                >
                  <Option value="consultation">Consultation</Option>
                  <Option value="session">Session</Option>
                </Select>
              </Form.Item>

              {appointmentFor === "session" && (
                <Form.Item
                  label="Choose Consultation"
                  name="op_admission"
                  required
                  className={styles.consultationField}
                  rules={[
                    {
                      required: true,
                      message:
                        "Please select the consultation for this session",
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={opAdmissionsLoading}
                    placeholder={
                      selectedId
                        ? "Select consultation"
                        : "Search and select a patient first"
                    }
                    disabled={!selectedId}
                    className={styles.select}
                    options={opAdmissions.map((admission) => ({
                      value: admission.id,
                      label: getOpAdmissionLabel(admission),
                    }))}
                  />
                </Form.Item>
              )}
            </div>

            <div className={styles.dateRow}>
              <div className={styles.sectionLabel}>Select Date</div>
              <div className={styles.dateStrip}>
                {quickDates.map((d) => {
                  const isActive =
                    selectedDate && d.isSame(selectedDate, "day");
                  return (
                    <button
                      type="button"
                      key={d.format("YYYY-MM-DD")}
                      className={`${styles.datePill} ${
                        isActive ? styles.datePillActive : ""
                      }`}
                      onClick={() => {
                        setCustomDateOpen(false);
                        form.setFieldValue("appointment_date", d);
                        setFormValues((prev) => ({
                          ...(prev || {}),
                          appointment_date: d,
                        }));
                        if (selectedPractitioner) setLoadingSlots(true);
                      }}
                    >
                      <div className={styles.dateDow}>
                        {d.format("ddd").toUpperCase()}
                      </div>
                      <div className={styles.dateNum}>{d.format("D")}</div>
                      <div className={styles.dateMon}>{d.format("MMM")}</div>
                    </button>
                  );
                })}

                <div className={styles.customDateWrap}>
                  <button
                    type="button"
                    className={`${styles.datePill} ${styles.customDatePill} ${
                      isCustomDateSelected
                        ? `${styles.datePillActive} ${styles.customDateSelected}`
                        : ""
                    } ${customDateOpen ? styles.customDatePillOpen : ""}`}
                    onClick={() => setCustomDateOpen(true)}
                  >
                    {isCustomDateSelected ? (
                      <>
                        <div className={styles.dateDow}>
                          {selectedDate.format("ddd").toUpperCase()}
                        </div>
                        <div className={styles.dateNum}>
                          {selectedDate.format("D")}
                        </div>
                        <div className={styles.dateMon}>
                          {selectedDate.format("MMM")}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.customDateIcon}>
                          <CalendarOutlined />
                        </div>
                        <div className={styles.customDateText}>Custom Date</div>
                      </>
                    )}
                  </button>
                  <DatePicker
                    {...mobilePickerProps}
                    disabledDate={disableApptDate}
                    open={customDateOpen}
                    onOpenChange={setCustomDateOpen}
                    value={selectedDate}
                    onChange={(d) => {
                      setCustomDateOpen(false);
                      form.setFieldValue("appointment_date", d);
                      setFormValues((prev) => ({
                        ...(prev || {}),
                        appointment_date: d,
                      }));
                      if (selectedPractitioner) setLoadingSlots(true);
                    }}
                    className={styles.customDatePickerAnchor}
                    inputReadOnly={isMobile}
                    allowClear={false}
                    format="DD-MM-YYYY"
                    placement={isMobile ? "bottomLeft" : "bottomRight"}
                    getPopupContainer={() => document.body}
                  />
                </div>
              </div>
            </div>

            <div className={styles.slotsSection}>
              <div className={styles.sectionLabel}>Available Time Slots</div>

              {!selectedPractitioner || !selectedDate ? (
                <div className={styles.slotsHint}>
                  Select facility, doctor, and date to see available slots.
                </div>
              ) : loadingSlots ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <Spin />
                </div>
              ) : sortedSlots.length === 0 ? (
                <Empty description="No slots available for selected date" />
              ) : (
                <div className={styles.slotGrid}>
                  {sortedSlots.map((s, idx) => {
                    const disabled = s?.status === "scheduled";
                    const active = selectedSlot === s?.appointment_time;
                    return (
                      <button
                        type="button"
                        key={`${idx}-${s?.appointment_time}`}
                        className={`${styles.slotBtn} ${
                          active ? styles.slotBtnActive : ""
                        }`}
                        disabled={disabled}
                        onClick={() => {
                          setSelectedSlot(s?.appointment_time);
                          setSelectedEndSlot(s?.appointment_end_time);
                          form.setFieldValue(
                            "appointment_time",
                            s?.appointment_time
                          );
                        }}
                      >
                        {formatSlotTime(s?.appointment_time)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Form.Item
              label="Reason For Visit"
              style={{ marginTop: 12 }}
              name="reason_to_visit"
            >
              <Input.TextArea
                placeholder="Enter reason for visit..."
                rows={3}
              />
            </Form.Item>
          </div>

          <div className={styles.footerWrap}>
            <div className={styles.footerRow}>
              <Button className={btnStyles.mdBtn} onClick={closeAndReset}>
                Cancel
              </Button>
              <Tooltip
                title={
                  missingForBooking.length
                    ? `Still needed: ${missingForBooking.join(", ")}`
                    : ""
                }
              >
                {/* span keeps the tooltip working while the button is disabled */}
                <span>
                  <Button
                    type="primary"
                    loading={loading}
                    className={btnStyles.mdBtn}
                    onClick={() => formRef.current?.submit()}
                    disabled={missingForBooking.length > 0}
                  >
                    Confirm booking
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AppointsModal;
